<template>
    <div class="page-customized">
        <v-progress-linear
            v-if="overlay"
            absolute
            indeterminate
        ></v-progress-linear>

        <app-bar
            :title="content.exists() ? t('club_content_title_update') : t('club_content_title_create')"
            back="/club-contents"
        />

        <v-layout class="mt-n8">
            <v-card
                width="100%"
                color="transparent"
                elevation="0"
                class="pb-8"
            >
                <v-card-text class="pb-0">
                    <v-form class="mt-6">
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_content_label_type') }}</div>
                            <div class="horizontal-input">
                                <v-select
                                    v-model="content.type"
                                    :items="clubContentTypes()"
                                    required
                                    outlined
                                    append-icon="mdi-chevron-down"
                                    dense
                                    :error-messages="form.errors.get('type')"
                                ></v-select>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_content_label_name') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="content.name"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('name')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="content.name"
                                    :items="content.translations['name']"
                                    @touch="(v) => content.translations['name'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_content_label_description') }}</div>
                            <div class="horizontal-input">
                                <v-textarea
                                    v-model="content.description"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('description')"
                                ></v-textarea>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="content.description"
                                    :items="content.translations['description']"
                                    @touch="(v) => content.translations['description'] = v"
                                    text
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal" v-if="content.isVideo()">
                            <div class="horizontal-label">{{ t('club_content_label_video_url') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="content.videoUrl"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('video_url')"
                                ></v-text-field>
                            </div>
                        </div>
                        <mux-playback-id-field
                            v-if="content.isVideo()"
                            v-model="content.muxPlaybackId"
                            :label="t('club_content_label_mux_playback_id')"
                            :errors="form.errors.get('mux_playback_id')"
                        ></mux-playback-id-field>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_content_label_icon') }}</div>
                            <div class="horizontal-input">
                                <v-file-input
                                    v-model="content.photo"
                                    outlined
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-paperclip"
                                    :append-outer-icon="content.photoUrl ? 'mdi-eye-outline' : ''"
                                    dense
                                    :error-messages="photoForm.errors.get('photo')"
                                    @click:append-outer="viewPhoto(content.photoUrl)"
                                    @click:clear="content.photo = undefined"
                                ></v-file-input>
                            </div>
                        </div>
                        <div class="form-group-horizontal" v-if="content.photos && content.photos.length">
                            <v-sheet
                                v-for="(photo, i) in content.photos"
                                :key="photo"
                                color="white"
                                elevation="0"
                                height="200"
                                width="200"
                                class="float-left mr-8 mb-8 bordered position-relative"
                            >
                                <v-img
                                    :lazy-src="photo"
                                    :src="photo"
                                    max-height="150"
                                    max-width="150"
                                    class="mx-auto mt-6"
                                ></v-img>
                                <confirm-modal
                                    :title="t('general_title_confirm_action')"
                                    @agree="content.delPhoto(i)"
                                >
                                    <template #default="{ on, attrs }">
                                        <v-btn
                                            v-on="on"
                                            v-bind="attrs"
                                            plain
                                            icon
                                            absolute
                                            top
                                            right
                                            class="mt-n5 mr-n5"
                                        ><v-icon color="orange">mdi-close</v-icon></v-btn>
                                    </template>
                                </confirm-modal>
                                <v-btn
                                    v-if="i !== 0"
                                    @click="content.updatePhotoNumber(i, (i - 1))"
                                    plain
                                    icon
                                    absolute
                                    bottom
                                    left
                                    class="mb-n5 ml-n5"
                                ><v-icon color="customized-primary">mdi-arrow-left</v-icon></v-btn>
                                <v-btn
                                    v-if="i !== content.photos.length - 1"
                                    @click="content.updatePhotoNumber(i, (i + 1))"
                                    plain
                                    icon
                                    absolute
                                    bottom
                                    right
                                    class="mb-n5 mr-n5"
                                ><v-icon color="customized-primary">mdi-arrow-right</v-icon></v-btn>
                            </v-sheet>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_content_label_header_photos') }}</div>
                            <div class="horizontal-input">
                                <v-file-input
                                    v-model="content.photoUps"
                                    outlined
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-paperclip"
                                    dense
                                    multiple
                                    :error-messages="photoForm.errors.get('photos.0') || photoForm.errors.get('photos')"
                                    @click:clear="content.photoUps = undefined"
                                ></v-file-input>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_content_label_link_text') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="content.linkText"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('link_text')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="content.linkText"
                                    :items="content.translations['link_text']"
                                    @touch="(v) => content.translations['link_text'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_link_mode') }}</div>
                            <div class="horizontal-input pb-6">
                                <v-btn-toggle
                                    v-model="linkMode"
                                    active-class="primary"
                                    class="my-1"
                                    dense
                                    mandatory
                                >
                                    <v-btn
                                        value="external"
                                        class="exs mr-3"
                                        small
                                    >{{ t('club_product_label_link_external') }}</v-btn>
                                    <v-btn
                                        value="dynamic"
                                        class="exs"
                                        small
                                    >{{ t('club_product_label_link_dynamic') }}</v-btn>
                                </v-btn-toggle>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t(linkMode === 'external' ? 'club_product_label_link_url' : 'club_content_label_link_params') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-if="linkMode === 'external'"
                                    v-model="content.link"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('link')"
                                ></v-text-field>
                                <template v-else>
                                    <div
                                        v-for="(row, i) in linkParamRows"
                                        :key="`param_${i}`"
                                        class="d-flex mb-2"
                                    >
                                        <v-text-field
                                            v-model="row.key"
                                            :placeholder="t('club_product_label_param_key')"
                                            outlined
                                            dense
                                            hide-details
                                            class="mr-2"
                                        ></v-text-field>
                                        <v-text-field
                                            v-model="row.value"
                                            :placeholder="t('club_product_label_param_value')"
                                            outlined
                                            dense
                                            hide-details
                                            class="mr-2"
                                        ></v-text-field>
                                        <v-btn
                                            @click="linkParamRows.splice(i, 1)"
                                            icon
                                            class="mt-1"
                                        ><v-icon>mdi-close</v-icon></v-btn>
                                    </div>
                                    <v-btn
                                        @click="linkParamRows.push({ key: '', value: '' })"
                                        v-ripple="false"
                                        plain
                                        color="orange darken-4"
                                        class="add mb-4 ml-1"
                                    ><v-icon left size="16">mdi-plus-circle</v-icon>{{ t('club_product_action_add_param') }}</v-btn>
                                </template>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('general_action_published') }}</div>
                            <div class="horizontal-input">
                                <v-switch
                                    class="ma-0"
                                    v-model="content.enabled"
                                ></v-switch>
                            </div>
                        </div>
                    </v-form>
                </v-card-text>

                <v-card-actions class="px-4">
                    <v-btn
                        :to="{ name: 'club-contents' }"
                        class="mr-4"
                        color="primary"
                    >{{ t('general_action_cancel') }}</v-btn>
                    <v-btn
                        :loading="form.busy"
                        :disabled="form.busy"
                        color="orange"
                        @click.prevent="submit"
                    >{{ content.exists() ? t('general_action_save') : t('club_content_title_create') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useForm } from '@/core/form'
    import { useEnums } from '@/core/defaults'
    import { ClubContent } from '@/core/models/club-content'
    import EditTranslationsModal from '@/components/EditTranslationsModal.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'
    import MuxPlaybackIdField from '@/components/MuxPlaybackIdField.vue'

    export default defineComponent({
        components: {
            MuxPlaybackIdField,
            EditTranslationsModal,
            ConfirmModal,
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id'] || undefined

            const state = reactive({
                overlay: true,
                linkParamRows: <{ key: string, value: string }[]>[],
                linkMode: 'external',
            })

            const contentModel = computed(() => <ClubContent>store.getters['clubContent/get'])

            const form = reactive(useForm())
            const photoForm = reactive(useForm())

            watch(() => state.linkMode, (mode) => {
                if (mode === 'dynamic' && ! state.linkParamRows.length) {
                    state.linkParamRows.push({ key: '', value: '' })
                }
            })

            const capture = () => {
                store.dispatch('clubContent/getOrNew', { id: id, payload: { append: 'translations' } }).then(() => {
                    state.linkParamRows = Object.entries(contentModel.value.linkParams || {}).map(([key, value]) => ({ key, value: <string>value }))
                    state.linkMode = state.linkParamRows.length > 0 ? 'dynamic' : 'external'
                    state.overlay = false
                })
            }

            const submit = () => {
                contentModel.value.linkParams = state.linkMode === 'dynamic'
                    ? state.linkParamRows.filter((row) => row.key).reduce((acc: any, row) => { acc[row.key] = row.value; return acc }, {})
                    : {}

                form.usingSubmit(store.dispatch('clubContent/updateOrCreate', {
                    id: id,
                    payload: contentModel.value.payload()
                }))
                    .then(() => {
                        const savedId = <any>contentModel.value.id
                        const next = () => router.push({ name: 'club-contents.view', params: <any>{ id: savedId } })
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        if (contentModel.value.photo || contentModel.value.photoUps) {
                            const data = new FormData()
                            if (contentModel.value.photo) {
                                data.append('photo', contentModel.value.photo)
                            }
                            if (contentModel.value.photoUps) {
                                contentModel.value.photoUps.map((i) => data.append('photos[]', i))
                            }
                            photoForm.usingSubmit(store.dispatch('clubContent/photo', { id: savedId, payload: data }).then(() => {
                                next()
                            }))
                        } else {
                            next()
                        }
                    })
            }

            const viewPhoto = (url: string) => {
                window.open(url)
            }

            onMounted(() => {
                capture()
            })

            return {
                form,
                photoForm,
                capture,
                viewPhoto,
                submit,
                content: contentModel.value,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
